import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Trash2, Edit2, Plus, Eye, Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FREQUENCY_LABELS = {
  instant: 'Instant',
  daily: 'Daily digest',
  weekly: 'Weekly digest',
  off: 'No alerts',
};

const SavedSearchCard = ({ search, onDelete, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(search.name);
  const [freq, setFreq] = useState(search.alert_frequency || 'daily');

  const save = async () => {
    try {
      await axios.patch(`${API_URL}/api/saved-searches/${search.id}`, { name, alert_frequency: freq });
      toast.success('Updated');
      setEditing(false);
      onUpdate();
    } catch {
      toast.error('Failed to update');
    }
  };

  const toggleActive = async () => {
    try {
      await axios.patch(`${API_URL}/api/saved-searches/${search.id}`, { active: !search.active });
      onUpdate();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const filterChips = [
    search.city && `${search.city}`,
    search.country,
    search.listing_type && `for ${search.listing_type}`,
    search.property_type,
    search.bedrooms && `${search.bedrooms}+ bed`,
    search.min_price && `min ${search.min_price}`,
    search.max_price && `max ${search.max_price}`,
  ].filter(Boolean);

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid={`saved-search-${search.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="text-base font-bold text-slate-900 border-b border-slate-300 w-full pb-1 focus:outline-none focus:border-[#E07A3A]"
              maxLength={80}
              data-testid={`saved-search-name-input-${search.id}`}
            />
          ) : (
            <h3 className="text-base font-bold text-slate-900 truncate">{search.name}</h3>
          )}
          <p className="text-xs text-slate-500 mt-1">
            Saved {new Date(search.created_at).toLocaleDateString()} · {search.total_match_count || 0} matches
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleActive}
            className={`p-1.5 ${search.active ? 'text-[#E07A3A]' : 'text-slate-300'} hover:bg-slate-50`}
            title={search.active ? 'Pause alerts' : 'Resume alerts'}
            data-testid={`toggle-alerts-${search.id}`}
          >
            {search.active ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setEditing(s => !s)}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50"
            title="Edit"
            data-testid={`edit-saved-${search.id}`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(search)}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50"
            title="Delete"
            data-testid={`delete-saved-${search.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {filterChips.map((chip, i) => (
          <span key={chip || `chip-${i}`} className="text-[11px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-1">
            {chip}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
        {editing ? (
          <>
            <select
              value={freq}
              onChange={e => setFreq(e.target.value)}
              className="text-xs border border-slate-300 px-2 py-1.5"
              data-testid={`saved-freq-${search.id}`}
            >
              {Object.entries(FREQUENCY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              onClick={save}
              className="px-3 py-1.5 text-xs uppercase tracking-wider bg-[#E07A3A] text-white hover:bg-[#C9682E]"
              data-testid={`save-saved-${search.id}`}
            >
              Save
            </button>
          </>
        ) : (
          <>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              {search.active ? <Bell className="h-3 w-3 text-[#E07A3A]" /> : <BellOff className="h-3 w-3 text-slate-300" />}
              {FREQUENCY_LABELS[search.alert_frequency] || 'Daily digest'}
            </span>
            <Link
              to={`/saved-searches/${search.id}`}
              className="px-3 py-1.5 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5"
              data-testid={`view-matches-${search.id}`}
            >
              <Eye className="h-3 w-3" />
              {search.new_match_count > 0 ? (
                <span><span className="text-[#E07A3A] font-bold">{search.new_match_count} new</span> · view</span>
              ) : 'View matches'}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const SavedSearchesTab = () => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/saved-searches`);
      setSearches(r.data.items || []);
    } catch {
      toast.error('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSearch = async (s) => {
    if (!window.confirm(`Delete saved search "${s.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/saved-searches/${s.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <div className="bg-white border border-slate-200 p-12 text-center" data-testid="saved-searches-empty">
        <Search className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <h3 className="font-['Playfair_Display'] text-xl text-slate-900 mb-2">No saved searches yet</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
          Search properties with your filters, then click "Save this search" to get email alerts when new matches appear.
        </p>
        <Link
          to="/properties"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E07A3A] text-white text-xs uppercase tracking-wider hover:bg-[#C9682E]"
          data-testid="browse-properties-cta"
        >
          <Plus className="h-3 w-3" /> Browse properties
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="saved-searches-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-900">Your saved searches</h3>
          <p className="text-xs text-slate-500 mt-1">{searches.length} active · we email you when new matches appear</p>
        </div>
        <Link
          to="/properties"
          className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50"
          data-testid="add-saved-search-cta"
        >
          <Plus className="h-3 w-3" /> Add new
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {searches.map(s => (
          <SavedSearchCard
            key={s.id}
            search={s}
            onDelete={deleteSearch}
            onUpdate={load}
          />
        ))}
      </div>
    </div>
  );
};
